import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { unwrapResult } from '@reduxjs/toolkit';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { errorNormalizer } from '../../../shared/Helpers/functions';
import { useResetCalendarData } from '../../Calendar/Helpers/hooks';
import { ACTIONS, behaviorTreatmentReducer, initialBehaviorTreatmentState } from './behaviorTreatmentReducer';
import BehaviorTreatmentData from './behaviorTreatmentData';
import { usePostSubmitEditMode } from '../helpers';

import { DispatchProperties, useSelector } from '../../../redux/store';
import { getSoapNote, updateBehaviorTreatment } from '../../../redux/State/clientSlice/soapNoteSlice';
import { setDate, setEndTime, setStartTime } from '../../../redux/State/clientSlice/sessionSlice';
import {
	BehaviorTreatmentProperties,
	UpdateBehaviorTreatmentProperties,
} from '../../../redux/API/ClientAPIHelpers/soapNoteProperties';

const BehaviorTreatment = () => {
	const dispatch = useDispatch<DispatchProperties>();
	const [params] = useSearchParams();
	const navigate = useNavigate();
	const { resetData } = useResetCalendarData();
	const { isPostSubmitEditMode } = usePostSubmitEditMode();

	const reportId = params.get('reportId');
	const clientId = params.get('clientId');

	const forcedOperation = params.get('forcedOperation') === 'true';

	const sessionInfo = useSelector((state) => state.session.sessionInfo);

	const [data, setData] = useReducer(behaviorTreatmentReducer, initialBehaviorTreatmentState);
	const [hydratedReportId, setHydratedReportId] = useState<string | null>(null);
	const currentUpdateRequestRef = useRef<ReturnType<typeof dispatch<any>> | null>(null);
	const isMounted = useRef(true);

	useEffect(() => {
		isMounted.current = true;

		return () => {
			isMounted.current = false;

			if (currentUpdateRequestRef.current) {
				console.info('Aborting request on unmount');
				currentUpdateRequestRef.current.abort();
			}
		};
	}, []);

	useEffect(() => {
		if (!reportId || !clientId) return;

		let active = true;
		setHydratedReportId(null);
		setData({ type: ACTIONS.setState, payload: initialBehaviorTreatmentState });

		const request = dispatch(getSoapNote({ reportId, clientId }));
		request
			.then(unwrapResult)
			.then((response) => {
				if (!active) return;

				const note = response as BehaviorTreatmentProperties;

				dispatch(setDate(note.date));
				dispatch(setStartTime(note.startTime));
				dispatch(setEndTime(note.endTime));

				setData({
					type: ACTIONS.setState,
					payload: {
						...note,
						barrierToTreatmentResponses: note.barrierToTreatmentResponses?.map((x) => x.id) ?? [],
						behaviorTreatmentTechniquesResponses:
							note.behaviorTreatmentTechniquesResponses?.map((x) => x.id) ?? [],
						promptLevelsResponses: note.promptLevelsResponses?.map((x) => x.id) ?? [],
						reinforcementResponses: note.reinforcementResponses?.map((x) => x.id) ?? [],
						skillDomainAddressedResponses: note.skillDomainAddressedResponses?.map((x) => x.id) ?? [],
						maladaptiveBehaviorTechniquesResponses:
							note.maladaptiveBehaviorTechniquesResponses?.map((x) => x.id) ?? [],
						clientMoodResponses: note.clientMoodResponses?.map((x) => x.id) ?? [],
						behaviorTreatmentFuturePlanResponses:
							note.behaviorTreatmentFuturePlanResponses?.map((x) => x.id) ?? [],
						participantResponses: note.participantResponses?.map((x) => x.id) ?? [],
						locationId: note.location?.id ?? null,
					},
				});
				setHydratedReportId(reportId);
			})
			.catch((error) => {
				if (!active) return;
				errorNormalizer(error);
				if (error.status === 403) navigate('/dashboard');
			});

		return () => {
			active = false;
			request.abort();
		};
	}, [dispatch, clientId, reportId, navigate]);

	const updateSoapNoteHandler = useCallback(
		(submit: boolean) => {
			if (currentUpdateRequestRef.current) {
				console.info('Aborting previous pending request');
				currentUpdateRequestRef.current.abort();
			}
			if (!reportId || hydratedReportId !== reportId || !clientId || !data) return;
			const { date, startTime, endTime } = sessionInfo;
			const allData: UpdateBehaviorTreatmentProperties = {
				...data,
				date,
				startTime,
				endTime,
				reportId,
				submit,
				forcedOperation,
				clientId,
			};

			const requestPromise = dispatch(updateBehaviorTreatment(allData));
			currentUpdateRequestRef.current = requestPromise;

			requestPromise
				.then(unwrapResult)
				.then(() => {
					if (!submit || !isMounted.current) return;
					resetData();
					toast('Success');
					navigate('/calendar');
				})
				.catch(errorNormalizer)
				.finally(() => {
					if (currentUpdateRequestRef.current === requestPromise) {
						currentUpdateRequestRef.current = null;
					}
				});
		},
		[reportId, hydratedReportId, clientId, forcedOperation, data, sessionInfo, navigate, resetData, dispatch],
	);

	const saveHandler = useCallback(() => {
		if (isPostSubmitEditMode) return;
		updateSoapNoteHandler(false);
	}, [updateSoapNoteHandler, isPostSubmitEditMode]);
	const submitHandler = useCallback(() => updateSoapNoteHandler(true), [updateSoapNoteHandler]);

	return (
		<BehaviorTreatmentData
			setData={setData}
			data={data}
			save={saveHandler}
			submit={submitHandler}
			hydratedReportId={hydratedReportId}
		/>
	);
};

export default BehaviorTreatment;
